/**
 * Settings Page — fully wired to Dexie
 *
 * Sections:
 *   1. Gym Profile — equipment toggles → updates GymProfile in DB
 *   2. Training    — weight unit, volume target, audio, split reset
 *   3. Injury Management — per-muscle injured toggle
 *   4. Data & Backup — JSON export / import with confirmation
 *   5. About
 */

import { useState, useRef, useCallback } from 'react';
import { useLiveQuery } from 'dexie-react-hooks';
import { db } from '@/lib/db';
import { repository } from '@/lib/DexieRepository';
import { setMuscleInjured } from '@/hooks/useFatigue';
import GymProfileManager from '@/components/GymProfileManager';
import type { MuscleGroup, AppSettings, ExportPayload, SplitType, SplitDay } from '@/types';

// ── Shared UI ────────────────────────────────────────────────

interface SettingRowProps {
  label: string;
  description?: string;
  children: React.ReactNode;
  border?: boolean;
}

function SettingRow({ label, description, children, border = true }: SettingRowProps) {
  return (
    <div className={`flex items-center justify-between gap-4 py-3.5
                     ${border ? 'border-b border-surface-400 last:border-0' : ''}`}>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-ink">{label}</p>
        {description && (
          <p className="text-xs text-ink-muted mt-0.5 leading-relaxed">{description}</p>
        )}
      </div>
      <div className="flex-shrink-0">{children}</div>
    </div>
  );
}

function Toggle({
  value,
  onChange,
}: {
  value: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <button
      role="switch"
      aria-checked={value}
      onClick={() => onChange(!value)}
      className={`w-12 h-7 rounded-full flex items-center px-0.5 transition-colors duration-200
                  min-w-tap min-h-tap active:scale-95
                  ${value ? 'bg-accent' : 'bg-surface-400'}`}
    >
      <div className={`w-6 h-6 rounded-full bg-white shadow transition-transform duration-200
                       ${value ? 'translate-x-5' : 'translate-x-0'}`} />
    </button>
  );
}

// ── Injury Management ────────────────────────────────────────

const INJURY_MUSCLES: { muscle: MuscleGroup; label: string }[] = [
  { muscle: 'chest',        label: 'Chest' },
  { muscle: 'front_delt',   label: 'Front Delt' },
  { muscle: 'lateral_delt', label: 'Lateral Delt' },
  { muscle: 'rear_delt',    label: 'Rear Delt' },
  { muscle: 'triceps',      label: 'Triceps' },
  { muscle: 'biceps',       label: 'Biceps' },
  { muscle: 'forearms',     label: 'Forearms' },
  { muscle: 'lats',         label: 'Lats' },
  { muscle: 'traps',        label: 'Traps' },
  { muscle: 'rhomboids',    label: 'Rhomboids' },
  { muscle: 'lower_back',   label: 'Lower Back' },
  { muscle: 'quads',        label: 'Quads' },
  { muscle: 'hamstrings',   label: 'Hamstrings' },
  { muscle: 'glutes',       label: 'Glutes' },
  { muscle: 'calves',       label: 'Calves' },
  { muscle: 'core',         label: 'Core' },
  { muscle: 'hip_flexors',  label: 'Hip Flexors' },
];

function InjurySection() {
  const [expanded, setExpanded] = useState(false);

  const muscleStates = useLiveQuery(() => db.muscleStates.toArray(), []) ?? [];
  const injuredMap = new Map(muscleStates.map((s) => [s.muscle, s.isInjured]));
  const injuredCount = [...injuredMap.values()].filter(Boolean).length;

  return (
    <section>
      <p className="section-header">Injury Exclusions</p>
      <div className="card space-y-0">
        <div className="pb-3">
          <p className="text-xs text-ink-muted leading-relaxed">
            Injured muscles are treated as fully fatigued (F=1.0) and skipped by the
            generator entirely — including all exercises that use them as synergists.
          </p>
          {injuredCount > 0 && (
            <div className="mt-2 badge-danger inline-flex gap-1">
              ⚠ {injuredCount} muscle{injuredCount > 1 ? 's' : ''} excluded
            </div>
          )}
        </div>

        <button
          className="btn-secondary w-full text-sm mb-0"
          onClick={() => setExpanded((v) => !v)}
        >
          {expanded ? 'Hide' : 'Manage'} Muscle Exclusions
          <span className={`ml-auto transition-transform ${expanded ? 'rotate-180' : ''}`}>›</span>
        </button>

        {expanded && (
          <div className="border-t border-surface-400 pt-3 mt-3 space-y-0 animate-fade-in">
            {INJURY_MUSCLES.map(({ muscle, label }) => (
              <SettingRow key={muscle} label={label}>
                <Toggle
                  value={injuredMap.get(muscle) ?? false}
                  onChange={(v) => setMuscleInjured(muscle, v)}
                />
              </SettingRow>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}

// ── Training Section ─────────────────────────────────────────

function TrainingSection({ settings }: { settings: AppSettings }) {
  const patch = useCallback(async (p: Partial<AppSettings>) => {
    await repository.updateSettings(p);
  }, []);

  const resetSplit = useCallback(async () => {
    const splitType = (settings.splitType ?? 'push_pull') as SplitType;
    const firstDay: Record<SplitType, SplitDay> = {
      push_pull: 'push', push_pull_legs: 'push', upper_lower: 'upper', full_body: 'full_body_day',
    };
    await repository.updateSettings({ nextSplitDay: firstDay[splitType] });
  }, [settings.splitType]);

  return (
    <section>
      <p className="section-header">Training</p>
      <div className="card space-y-0">
        {/* Weight unit */}
        <SettingRow label="Weight Unit">
          <div className="flex bg-surface-300 rounded-xl p-0.5 gap-0.5">
            {(['lbs', 'kg'] as const).map((u) => (
              <button
                key={u}
                onClick={() => patch({ weightUnit: u })}
                className={`px-4 py-1.5 rounded-lg text-sm font-medium transition-colors min-h-tap
                  ${settings.weightUnit === u ? 'bg-accent text-white' : 'text-ink-muted'}`}
              >
                {u}
              </button>
            ))}
          </div>
        </SettingRow>

        {/* Weekly volume target */}
        <SettingRow label="Weekly Volume Target"
          description="Hard sets per muscle per 7-day window (10–20 rec.)">
          <div className="flex items-center gap-3">
            <button
              className="w-9 h-9 rounded-xl bg-surface-300 text-ink font-bold text-lg
                         flex items-center justify-center active:scale-90 transition-transform"
              onClick={() => patch({ weeklyVolumeTarget: Math.max(10, settings.weeklyVolumeTarget - 1) })}
            >−</button>
            <span className="text-ink font-bold font-mono w-6 text-center">
              {settings.weeklyVolumeTarget}
            </span>
            <button
              className="w-9 h-9 rounded-xl bg-surface-300 text-ink font-bold text-lg
                         flex items-center justify-center active:scale-90 transition-transform"
              onClick={() => patch({ weeklyVolumeTarget: Math.min(20, settings.weeklyVolumeTarget + 1) })}
            >+</button>
          </div>
        </SettingRow>

        {/* Session set cap */}
        <SettingRow label="Session Set Cap"
          description="Max working sets per session (20–24 rec.)">
          <div className="flex items-center gap-3">
            <button
              className="w-9 h-9 rounded-xl bg-surface-300 text-ink font-bold text-lg
                         flex items-center justify-center active:scale-90 transition-transform"
              onClick={() => patch({ sessionSetCap: Math.max(16, settings.sessionSetCap - 1) })}
            >−</button>
            <span className="text-ink font-bold font-mono w-6 text-center">
              {settings.sessionSetCap}
            </span>
            <button
              className="w-9 h-9 rounded-xl bg-surface-300 text-ink font-bold text-lg
                         flex items-center justify-center active:scale-90 transition-transform"
              onClick={() => patch({ sessionSetCap: Math.min(30, settings.sessionSetCap + 1) })}
            >+</button>
          </div>
        </SettingRow>

        {/* Rest timer audio */}
        <SettingRow label="Rest Timer Audio"
          description="Beep cue when rest period ends">
          <Toggle value={settings.audioEnabled} onChange={(v) => patch({ audioEnabled: v })} />
        </SettingRow>

        {/* Training goal */}
        <SettingRow label="Training Goal">
          <select
            value={settings.trainingGoal}
            onChange={(e) => patch({ trainingGoal: e.target.value as import('@/types').TrainingGoal })}
            className="bg-surface-300 rounded-xl px-3 py-2 text-sm text-ink
                       focus:outline-none focus:ring-2 focus:ring-accent min-h-tap"
          >
            <option value="strength">Strength</option>
            <option value="hypertrophy">Hypertrophy</option>
            <option value="general_fitness">General Fitness</option>
          </select>
        </SettingRow>

        {/* Experience level */}
        <SettingRow label="Experience Level">
          <select
            value={settings.experienceLevel}
            onChange={(e) => patch({ experienceLevel: e.target.value as import('@/types').ExperienceLevel })}
            className="bg-surface-300 rounded-xl px-3 py-2 text-sm text-ink
                       focus:outline-none focus:ring-2 focus:ring-accent min-h-tap"
          >
            <option value="beginner">Beginner</option>
            <option value="intermediate">Intermediate</option>
            <option value="advanced">Advanced</option>
          </select>
        </SettingRow>

        {/* Preferred duration */}
        <SettingRow label="Session Length">
          <div className="flex bg-surface-300 rounded-xl p-0.5 gap-0.5">
            {([30, 45, 60, 75] as const).map((d) => (
              <button
                key={d}
                onClick={() => patch({ preferredDurationMinutes: d })}
                className={`px-2.5 py-1.5 rounded-lg text-xs font-medium transition-colors min-h-tap
                  ${settings.preferredDurationMinutes === d ? 'bg-accent text-white' : 'text-ink-muted'}`}
              >
                {d}m
              </button>
            ))}
          </div>
        </SettingRow>

        {/* Training split type */}
        <SettingRow label="Training Split"
          description="How your sessions cycle through muscle groups">
          <select
            value={settings.splitType ?? 'push_pull'}
            onChange={async (e) => {
              const type = e.target.value as SplitType;
              const firstDay: Record<SplitType, SplitDay> = {
                push_pull:      'push',
                push_pull_legs: 'push',
                upper_lower:    'upper',
                full_body:      'full_body_day',
              };
              await patch({ splitType: type, nextSplitDay: firstDay[type] });
            }}
            className="bg-surface-300 rounded-xl px-3 py-2 text-sm text-ink
                       focus:outline-none focus:ring-2 focus:ring-accent min-h-tap"
          >
            <option value="push_pull">Push / Pull</option>
            <option value="push_pull_legs">Push / Pull / Legs</option>
            <option value="upper_lower">Upper / Lower</option>
            <option value="full_body">Full Body</option>
          </select>
        </SettingRow>

        {/* Next split day */}
        {(() => {
          const splitType = (settings.splitType ?? 'push_pull') as SplitType;
          const dayOptions: Record<SplitType, { val: SplitDay; lbl: string }[]> = {
            push_pull:      [{ val: 'push', lbl: 'Push' }, { val: 'pull', lbl: 'Pull' }],
            push_pull_legs: [{ val: 'push', lbl: 'Push' }, { val: 'pull', lbl: 'Pull' }, { val: 'legs', lbl: 'Legs' }],
            upper_lower:    [{ val: 'upper', lbl: 'Upper' }, { val: 'lower', lbl: 'Lower' }],
            full_body:      [{ val: 'full_body_day', lbl: 'Full Body' }],
          };
          const options = dayOptions[splitType];
          if (options.length <= 1) return null;
          return (
            <SettingRow label="Next Session Day"
              description="Override which day in the cycle comes next">
              <div className="flex bg-surface-300 rounded-xl p-0.5 gap-0.5">
                {options.map(({ val, lbl }) => (
                  <button
                    key={val}
                    onClick={() => patch({ nextSplitDay: val })}
                    className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors min-h-tap
                      ${settings.nextSplitDay === val ? 'bg-accent text-white' : 'text-ink-muted'}`}
                  >
                    {lbl}
                  </button>
                ))}
              </div>
            </SettingRow>
          );
        })()}

        {/* Appearance — dark / light mode */}
        <SettingRow label="Appearance">
          <div className="flex bg-surface-300 rounded-xl p-0.5 gap-0.5">
            {([['dark', '🌙 Dark'], ['light', '☀️ Light']] as const).map(([val, lbl]) => (
              <button
                key={val}
                onClick={() => patch({ theme: val })}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors min-h-tap
                  ${settings.theme === val ? 'bg-accent text-white' : 'text-ink-muted'}`}
              >
                {lbl}
              </button>
            ))}
          </div>
        </SettingRow>

        {/* Reset split sequence */}
        <div className="pt-1">
          <button
            className="btn-ghost text-sm text-ink-faint w-full justify-start"
            onClick={resetSplit}
          >
            Reset to first day of cycle →
          </button>
        </div>
      </div>
    </section>
  );
}

// ── Notifications Section ─────────────────────────────────────

function NotificationsSection({ settings }: { settings: AppSettings }) {
  const [permStatus, setPermStatus] = useState<NotificationPermission>(
    'Notification' in window ? Notification.permission : 'denied'
  );
  const [toast, setToast] = useState<string | null>(null);
  const patch = useCallback((p: Partial<AppSettings>) => repository.updateSettings(p), []);

  const showToast = (msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(null), 3000);
  };

  const handleToggle = async (enabled: boolean) => {
    if (enabled && permStatus !== 'granted') {
      const { requestNotificationPermission, scheduleWorkoutReminder } = await import('@/lib/notifications');
      const perm = await requestNotificationPermission();
      setPermStatus(perm);
      if (perm !== 'granted') { showToast('Notification permission denied by browser.'); return; }
      await patch({ notificationsEnabled: true });
      scheduleWorkoutReminder(settings.notificationTime ?? '08:00');
      showToast('✓ Workout reminders enabled');
    } else if (enabled) {
      const { scheduleWorkoutReminder } = await import('@/lib/notifications');
      await patch({ notificationsEnabled: true });
      scheduleWorkoutReminder(settings.notificationTime ?? '08:00');
      showToast('✓ Workout reminders enabled');
    } else {
      const { cancelWorkoutReminder } = await import('@/lib/notifications');
      await patch({ notificationsEnabled: false });
      cancelWorkoutReminder();
      showToast('Reminders turned off');
    }
  };

  const handleTimeChange = async (time: string) => {
    await patch({ notificationTime: time });
    if (settings.notificationsEnabled && permStatus === 'granted') {
      const { scheduleWorkoutReminder } = await import('@/lib/notifications');
      scheduleWorkoutReminder(time);
    }
  };

  if (!('Notification' in window)) return null;

  return (
    <section>
      <p className="section-header">Workout Reminders</p>
      <div className="card space-y-0">
        <SettingRow label="Daily Reminder"
          description={permStatus === 'denied' ? 'Blocked in browser settings' : 'Get a push notification to train'}>
          <Toggle
            value={settings.notificationsEnabled ?? false}
            onChange={handleToggle}
          />
        </SettingRow>

        {(settings.notificationsEnabled ?? false) && permStatus === 'granted' && (
          <SettingRow label="Reminder Time">
            <input
              type="time"
              value={settings.notificationTime ?? '08:00'}
              onChange={(e) => handleTimeChange(e.target.value)}
              className="bg-surface-300 rounded-xl px-3 py-2 text-sm text-ink
                         focus:outline-none focus:ring-2 focus:ring-accent font-mono"
            />
          </SettingRow>
        )}
      </div>

      {toast && (
        <div className="fixed bottom-24 left-1/2 -translate-x-1/2 z-50
                        bg-surface-200 border border-surface-400 rounded-2xl
                        px-5 py-3 text-sm text-ink shadow-card-lg
                        animate-fade-in whitespace-nowrap">
          {toast}
        </div>
      )}
    </section>
  );
}

// ── Fitbod CSV Importer ───────────────────────────────────────

/**
 * Fitbod exports CSV with columns:
 * Date, Workout Name, Duration, Exercise Name, Set Order,
 * Weight (lbs), Reps, Distance (miles), Seconds, Notes, Workout Notes
 */
function parseCSVRow(row: string): string[] {
  const result: string[] = [];
  let cur = '';
  let inQuotes = false;
  for (let i = 0; i < row.length; i++) {
    const c = row[i];
    if (c === '"') { inQuotes = !inQuotes; }
    else if (c === ',' && !inQuotes) { result.push(cur.trim()); cur = ''; }
    else { cur += c; }
  }
  result.push(cur.trim());
  return result;
}

async function importFitbodCSV(text: string): Promise<{ sessions: number; sets: number; skipped: number }> {
  const lines = text.split(/\r?\n/).filter(Boolean);
  if (lines.length < 2) throw new Error('CSV appears empty');

  const header = parseCSVRow(lines[0]).map((h) => h.toLowerCase().trim());
  const idx = (name: string) => header.findIndex((h) => h.includes(name));

  const dateCol     = idx('date');
  const exNameCol   = idx('exercise name');
  const setOrderCol = idx('set order');
  const weightCol   = idx('weight');
  const repsCol     = idx('reps');
  const durationCol = idx('duration');
  const notesCol    = idx('workout notes');

  if (dateCol < 0 || exNameCol < 0) {
    throw new Error('Not a Fitbod export — missing Date or Exercise Name columns');
  }

  // Load existing exercises for name matching
  const allExercises = await db.exercises.toArray();
  const exByName = new Map(allExercises.map((e) => [e.name.toLowerCase(), e]));

  // Parse all data rows
  interface FitbodRow {
    date: string;
    workoutName: string;
    duration: number;
    exerciseName: string;
    setOrder: number;
    weightLbs: number;
    reps: number;
    notes: string;
  }

  const rows: FitbodRow[] = [];
  for (let i = 1; i < lines.length; i++) {
    const cells = parseCSVRow(lines[i]);
    if (cells.length < 3) continue;
    const rawDate = cells[dateCol] ?? '';
    const exName  = cells[exNameCol] ?? '';
    if (!rawDate || !exName) continue;

    // Parse date — Fitbod uses "Jan 1, 2023" or ISO format
    let parsedDate: Date;
    try {
      parsedDate = new Date(rawDate);
      if (isNaN(parsedDate.getTime())) continue;
    } catch { continue; }

    const weightRaw = parseFloat(cells[weightCol] ?? '0');
    const repsRaw   = parseInt(cells[repsCol] ?? '0', 10);
    if (repsRaw <= 0) continue; // skip cardio/timed rows

    // Parse duration "45 minutes" → seconds
    const durStr = durationCol >= 0 ? (cells[durationCol] ?? '') : '';
    const durMatch = durStr.match(/(\d+)/);
    const durSeconds = durMatch ? parseInt(durMatch[1], 10) * 60 : 0;

    rows.push({
      date: parsedDate.toISOString().slice(0, 10),
      workoutName: cells[1] ?? '',
      duration: durSeconds,
      exerciseName: exName,
      setOrder: parseInt(cells[setOrderCol] ?? '1', 10) || 1,
      weightLbs: weightRaw,
      reps: repsRaw,
      notes: notesCol >= 0 ? (cells[notesCol] ?? '') : '',
    });
  }

  if (rows.length === 0) throw new Error('No valid sets found in CSV');

  // Group by date to create workout sessions
  const byDate = new Map<string, FitbodRow[]>();
  for (const row of rows) {
    const arr = byDate.get(row.date) ?? [];
    arr.push(row);
    byDate.set(row.date, arr);
  }

  let totalSets = 0;
  let skipped   = 0;

  for (const [date, dateRows] of byDate) {
    const workoutId = 'fitbod_' + date.replace(/-/g, '');
    // Skip if already imported
    const existing = await db.workouts.get(workoutId);
    if (existing) continue;

    // Determine duration from first row that has it
    const durSeconds = dateRows[0]?.duration ?? 0;

    const isoDate = new Date(date + 'T12:00:00').toISOString();
    const isoEnd  = new Date(date + 'T12:00:00').toISOString();

    await db.workouts.put({
      id: workoutId,
      startedAt: isoDate,
      completedAt: isoEnd,
      splitDay: 'full_body_day', // Fitbod doesn't map cleanly to our splits
      status: 'completed',
      gymProfileId: 'profile_default',
      durationSeconds: durSeconds,
      notes: dateRows[0]?.notes || undefined,
    });

    // Log sets for each exercise in this session
    for (const row of dateRows) {
      const nameLower = row.exerciseName.toLowerCase();
      const matchedEx = exByName.get(nameLower)
        ?? [...exByName.entries()].find(([k]) => k.includes(nameLower.slice(0, 10)) || nameLower.includes(k.slice(0, 10)))?.[1];

      if (!matchedEx) { skipped++; continue; }

      const setId = `fitbod_${date}_${matchedEx.id}_s${row.setOrder}`;
      const existing = await db.loggedSets.get(setId);
      if (existing) continue;

      const e1rm = row.reps > 0 ? row.weightLbs * (1 + row.reps / 30) : 0;
      await db.loggedSets.put({
        id: setId,
        workoutId,
        exerciseId: matchedEx.id,
        setNumber: row.setOrder,
        weightLbs: row.weightLbs,
        reps: row.reps,
        rpe: 8, // assume RPE 8 for imported sets
        rir: 2,
        loggedAt: isoDate,
        isWarmup: false,
        estimated1RM: Math.round(e1rm * 10) / 10,
      });
      totalSets++;
    }
  }

  return { sessions: byDate.size, sets: totalSets, skipped };
}

// ── Backup Engine ────────────────────────────────────────────

function BackupSection() {
  const [importing, setImporting] = useState(false);
  const [importingFitbod, setImportingFitbod] = useState(false);
  const [confirmErase, setConfirmErase] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const fitbodRef = useRef<HTMLInputElement>(null);

  const showToast = (msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(null), 3000);
  };

  // ── Export ────────────────────────────────────────────────

  const handleExport = async () => {
    try {
      const payload = await repository.exportAllData();
      const json = JSON.stringify(payload, null, 2);
      const blob = new Blob([json], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      const dateStr = new Date().toISOString().slice(0, 10);
      a.href = url;
      a.download = `flexengine-backup-${dateStr}.json`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      showToast('✓ Backup exported successfully');
    } catch (err) {
      showToast(`Export failed: ${err}`);
    }
  };

  // ── Fitbod Import ─────────────────────────────────────────

  const handleFitbodSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImportingFitbod(true);
    try {
      const text = await file.text();
      const result = await importFitbodCSV(text);
      showToast(
        `✓ Imported ${result.sessions} sessions, ${result.sets} sets` +
        (result.skipped > 0 ? ` (${result.skipped} sets skipped — exercise not found)` : '')
      );
    } catch (err) {
      showToast(`Fitbod import failed: ${String(err)}`);
    } finally {
      setImportingFitbod(false);
      if (fitbodRef.current) fitbodRef.current.value = '';
    }
  };

  // ── FlexEngine JSON Import ────────────────────────────────

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImporting(true);

    try {
      const text = await file.text();
      const payload: ExportPayload = JSON.parse(text);

      // Schema validation
      if (typeof payload.schemaVersion !== 'number') {
        throw new Error('Invalid backup file — missing schemaVersion');
      }
      if (!Array.isArray(payload.exercises) || !Array.isArray(payload.workouts)) {
        throw new Error('Invalid backup file — malformed data');
      }

      await repository.importAllData(payload);
      showToast(`✓ Imported ${payload.workouts.length} sessions, ${payload.exercises.length} exercises`);
    } catch (err) {
      showToast(`Import failed: ${String(err)}`);
    } finally {
      setImporting(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  // ── Erase ─────────────────────────────────────────────────

  const handleErase = async () => {
    try {
      await db.workouts.clear();
      await db.loggedSets.clear();
      // Re-seed muscle states and settings but keep exercises
      await repository.seed?.();
      setConfirmErase(false);
      showToast('✓ All workout data erased');
    } catch (err) {
      showToast(`Erase failed: ${err}`);
    }
  };

  return (
    <section>
      <p className="section-header">Data & Backup</p>
      <div className="card space-y-2">
        <button
          className="btn-secondary w-full text-sm justify-start gap-3"
          onClick={handleExport}
        >
          <span className="text-lg">📤</span> Export All Data (JSON)
        </button>

        <button
          className="btn-secondary w-full text-sm justify-start gap-3"
          onClick={() => fileRef.current?.click()}
          disabled={importing}
        >
          <span className="text-lg">📥</span>
          {importing ? 'Importing…' : 'Import Backup (JSON)'}
        </button>

        <div className="divider" />

        {/* Fitbod CSV importer */}
        <div className="bg-surface-200 rounded-xl px-3 py-2.5 space-y-1.5">
          <p className="text-xs font-semibold text-ink">Migrate from Fitbod</p>
          <p className="text-xs text-ink-muted leading-relaxed">
            In Fitbod: Profile → Export Workout Data → share the .csv file here.
            Your session history, sets, and 1RM data will carry over.
          </p>
          <button
            className="btn-secondary w-full text-sm justify-start gap-3 mt-1"
            onClick={() => fitbodRef.current?.click()}
            disabled={importingFitbod}
          >
            <span className="text-lg">🏃</span>
            {importingFitbod ? 'Importing from Fitbod…' : 'Import Fitbod CSV'}
          </button>
        </div>

        {/* Hidden file inputs */}
        <input
          ref={fileRef}
          type="file"
          accept=".json,application/json"
          className="hidden"
          onChange={handleFileSelect}
        />
        <input
          ref={fitbodRef}
          type="file"
          accept=".csv,text/csv"
          className="hidden"
          onChange={handleFitbodSelect}
        />

        <div className="divider" />

        <button
          className="btn-danger w-full text-sm justify-start gap-3"
          onClick={() => setConfirmErase(true)}
        >
          <span className="text-lg">🗑️</span> Erase All Workout Data
        </button>
      </div>

      {/* Erase confirmation modal */}
      {confirmErase && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-6 bg-black/70">
          <div className="card-elevated w-full max-w-xs space-y-4 p-6 text-center animate-fade-in">
            <p className="text-2xl">⚠️</p>
            <p className="font-bold text-ink text-lg">Erase All Data?</p>
            <p className="text-ink-muted text-sm">
              This permanently deletes all workout sessions and logged sets.
              Exercises and settings are preserved. Export a backup first.
            </p>
            <div className="flex gap-3">
              <button className="btn-secondary flex-1" onClick={() => setConfirmErase(false)}>
                Cancel
              </button>
              <button className="btn-danger flex-1" onClick={handleErase}>
                Erase
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Toast */}
      {toast && (
        <div className="fixed bottom-24 left-1/2 -translate-x-1/2 z-50
                        bg-surface-200 border border-surface-400 rounded-2xl
                        px-5 py-3 text-sm text-ink shadow-card-lg
                        animate-fade-in whitespace-nowrap">
          {toast}
        </div>
      )}
    </section>
  );
}

// ── Main Page ─────────────────────────────────────────────────

export default function SettingsPage() {
  const settings = useLiveQuery(() => db.settings.get('singleton'), []);

  if (!settings) {
    return (
      <div className="flex items-center justify-center h-40">
        <p className="text-ink-muted text-sm">Loading settings…</p>
      </div>
    );
  }

  return (
    <div className="content-pad-bottom px-4 pt-6 pb-8 space-y-6 animate-fade-in">
      <header>
        <p className="text-ink-muted text-sm">Preferences</p>
        <h1 className="text-2xl font-bold text-ink tracking-tight">Settings</h1>
      </header>

      <GymProfileManager />
      <TrainingSection settings={settings} />
      <NotificationsSection settings={settings} />
      <InjurySection />
      <BackupSection />

      {/* About */}
      <section>
        <p className="section-header">About</p>
        <div className="card space-y-0">
          <SettingRow label="Version">
            <span className="text-ink-muted text-sm font-mono">0.1.0-phase4</span>
          </SettingRow>
          <SettingRow label="Exercise Database">
            <span className="text-ink-muted text-sm">60 exercises</span>
          </SettingRow>
          <SettingRow label="Storage">
            <span className="text-ink-muted text-sm">Local · IndexedDB</span>
          </SettingRow>
          <SettingRow label="Offline">
            <span className="text-success text-sm font-medium">✓ Full support</span>
          </SettingRow>
        </div>
      </section>
    </div>
  );
}
